/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import styled from '@emotion/styled';
import { CT_THRESHOLDS } from '@resistapp/client/components/plots/chip-view-constant';
import { oldTheme } from '@resistapp/client/components/shared/old-styles';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { BarDatum, BoxDatum, getNumDetectedTotal } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useExperimentalMetric } from '@resistapp/client/hooks/use-experimental-metric';
import { useNormalisationMode, useXAxisMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { GeneGrouping, L2Targets } from '@resistapp/common/assays';
import { isAdmin } from '@resistapp/common/features';
import { friendlyAbundance, friendlyDate, friendlyL2Target, type StandardDateFormat } from '@resistapp/common/friendly';
import { NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { assessTechReps } from '@resistapp/common/qa';
import { CtsColumns, NormalisationMode, XAxisMode } from '@resistapp/common/types';
import { SeriesPoint } from '@visx/shape/lib/types';
import { ScaleOrdinal } from 'd3-scale';
import { useMemo } from 'react';
import { getGroupTextColor } from '../../shared/palettes';

export type BarTooltipData = {
  bar: SeriesPoint<BarDatum>;
  key: string;
  index: number;
  height: number;
  width: number;
  y: number;
  color: string;
  label?: string;
};

export function getAbundace(datum: BarTooltipData) {
  const val = datum.bar.data[datum.key] as number | undefined;
  return +(val ?? 0);
}

interface BarTooltipContentProps {
  tooltipData: BarTooltipData;
}

interface NumDetectedTooltipContentProps extends BarTooltipContentProps {
  tooltipData: BarTooltipData;
  colorScale: ScaleOrdinal<string, string>;
  grouping: GeneGrouping;
  showGeneInfo?: boolean;
}

export function NumDetectedTooltipContent({
  tooltipData,
  colorScale,
  grouping,
  showGeneInfo = false,
}: NumDetectedTooltipContentProps) {
  const { allGeneGroups } = useAssayContext();
  const xAxisMode = useXAxisMode();
  const total = getNumDetectedTotal(tooltipData.bar.data);
  const groupColor = colorScale(tooltipData.key);
  const groupTextColor = getGroupTextColor(tooltipData.key, grouping, allGeneGroups);
  const sampleLabel =
    xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? tooltipData.bar.data.bioNumber : tooltipData.bar.data.sampleNumber;

  const abundance = getAbundace(tooltipData);

  return (
    <div style={{ padding: 8 }}>
      <div>
        <strong>Sample {sampleLabel}:</strong> {tooltipData.bar.data.label}
        {tooltipData.bar.data.date &&
          ` on ${friendlyDate(tooltipData.bar.data.date as StandardDateFormat, 'prefixMon', true)}`}
      </div>
      <div style={{ marginTop: oldTheme.spacing.xs, fontSize: oldTheme.fontSize.s }}>
        <strong>{total} genes detected in total</strong> in selected targets
      </div>
      {showGeneInfo && (
        <div style={{ marginTop: oldTheme.spacing.min, fontSize: oldTheme.fontSize.s }}>
          <strong>
            {abundance}{' '}
            <div
              style={{
                backgroundColor: groupColor,
                color: groupTextColor,
                borderRadius: '6px',
                padding: '2px 2px',
                display: 'inline-block',
              }}
            >
              {tooltipData.label ||
                (tooltipData.key in L2Targets ? friendlyL2Target(tooltipData.key as L2Targets) : tooltipData.key)}
            </div>{' '}
            genes detected
          </strong>
        </div>
      )}
    </div>
  );
}

interface HeatmapTooltipContentProps extends BarTooltipContentProps {
  grouping: GeneGrouping;
  colorScale: ScaleOrdinal<string, string>;
}

export function HeatmapTooltipContent({ colorScale, tooltipData, grouping }: HeatmapTooltipContentProps) {
  const { allGeneGroups, getGroup, allAssays } = useAssayContext();
  const normalisationMode = useNormalisationMode();
  const valueName = NormalisedValueName[normalisationMode];
  const groupName = getGroup(tooltipData.key, grouping) as string;
  const groupColor = colorScale(groupName);
  const groupTextColor = getGroupTextColor(groupName, grouping, allGeneGroups);
  const value = getAbundace(tooltipData);
  const { project, adminFeatures } = useResearchContext();
  const { user } = useUser();
  const isAdminUser = isAdmin(user);
  const showAdmin = isAdminUser && adminFeatures.isModifyAsAdminActive;

  const adminInfo = useMemo(() => {
    if (!showAdmin || !project) return null;
    const assay = getGroup(tooltipData.key, 'assay') ?? tooltipData.key;
    const assayInfo = allAssays.find(a => a.assay === assay);
    const currentCtsByAssayFromData = tooltipData.bar.data.ctsByAssay;

    const ctsReps: CtsColumns[] =
      currentCtsByAssayFromData &&
      typeof currentCtsByAssayFromData === 'object' && // Ensure it's an object
      !Array.isArray(currentCtsByAssayFromData) && // Ensure it's not an array itself
      currentCtsByAssayFromData[assay] && // Ensure the key exists
      Array.isArray(currentCtsByAssayFromData[assay]) // Ensure the value for the key is an array
        ? currentCtsByAssayFromData[assay] // Now TypeScript should be more confident that this is CtReplicate[]
        : [];

    if (!ctsReps.length) return null;

    const tmMinLocal = assayInfo?.minMeltingTemperature ?? null;
    const tmMaxLocal = assayInfo?.maxMeltingTemperature ?? null;
    const qaRes = assessTechReps(
      ctsReps.map(r => ({ ct: r.ct ?? null, efficiency: r.efficiency ?? null, tm: r.tm ?? null })),
      assay,
      tmMinLocal,
      tmMaxLocal,
      CT_THRESHOLDS[5],
    );
    const finalReps = qaRes.reps;
    const stdCtFinal = qaRes.stdCt;

    const nbsp = '\u00A0';
    const spacer = `${nbsp}${nbsp}${nbsp}`; // three non-breaking spaces

    const buildStyledList = (vals: Array<number | null>, field: 'ct' | 'efficiency' | 'tm') =>
      finalReps.map((rep, idx) => {
        const style: React.CSSProperties = { fontFamily: 'monospace' };

        if (rep.rule != null) style.textDecoration = 'line-through';

        let color: string | undefined;
        if (field === 'efficiency') {
          color = rep.rule === 4 ? 'orange' : rep.rule === 2 ? 'red' : undefined;
        } else if (field === 'ct') {
          color = rep.rule === 3 ? 'orange' : rep.rule === 1 ? 'red' : undefined;
        } else if (field === 'tm') {
          color = rep.rule === 0 ? 'red' : undefined;
        }
        if (color) style.color = color;

        const val = vals[idx];
        const text = val == null ? '--.--' : val.toFixed(2);
        return (
          <span key={idx}>
            <code style={style}>
              {field === 'efficiency' && val != null ? '\u00A0' : ''}
              {text}
            </code>
            {idx < finalReps.length - 1 ? spacer : ''}
          </span>
        );
      });

    const ctsStyled = buildStyledList(
      finalReps.map(r => r.ct),
      'ct',
    );
    const effStyled = buildStyledList(
      finalReps.map(r => r.efficiency),
      'efficiency',
    );
    const tmStyled = buildStyledList(
      finalReps.map(r => r.tm),
      'tm',
    );

    // Use non-breaking spaces (Unicode \u00A0) to preserve spacing in rendered HTML.
    // We also replace comma separators with three non-breaking spaces for a cleaner look.

    // Build status numbers so that they align vertically with the other technical-rep
    // columns.  Each numeric entry is rendered inside a monospace <code> element and
    // left-padded with non-breaking spaces to four characters (matching the width of
    // the 5-character numeric fields plus the three-space separator used below).
    const statusStyled = finalReps.map((rep, idx) => {
      const paddedRule = nbsp + nbsp + String(rep.rule ?? nbsp) + nbsp + nbsp; // 4-char field
      return (
        <span key={idx}>
          <code style={{ fontFamily: 'monospace' }}>{paddedRule}</code>
          {idx < finalReps.length - 1 ? spacer : ''}
        </span>
      );
    });

    const tmMin = assayInfo?.minMeltingTemperature;
    const tmMax = assayInfo?.maxMeltingTemperature;
    const tmRangeText = tmMin == null && tmMax == null ? '' : ` (r: ${tmMin ?? ''}-${tmMax ?? ''})`;

    const flags = Array.from(
      new Set(ctsReps.flatMap((ctRep: CtsColumns) => ctRep.flags?.split(/[,;\s]+/).filter(Boolean) || [])),
    );
    const first: CtsColumns | undefined = ctsReps[0];
    // Add a check for first being undefined, as ctsReps could be empty (for pooled projects, though unlikely due to earlier check?)
    if (!first) return null;
    const chip = project.chips.find(c => c.id === first.chipId);
    const chipInfo = chip ? `${chip.name} (${first.row},${first.column})` : `(${first.row},${first.column})`;

    return {
      ctsStyled,
      effStyled,
      tmStyled,
      statusStyled,
      stdCt: stdCtFinal,
      flags,
      chipInfo,
      tmRangeText,
    };
  }, [showAdmin, tooltipData, getGroup, allAssays, project]);

  return (
    <div style={{ padding: 8 }}>
      <table style={{ width: '100%' }}>
        <tbody>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Group:</Label>
            <Value>
              <span
                style={{ backgroundColor: groupColor, color: groupTextColor, borderRadius: '6px', padding: '1px 4px' }}
              >
                {groupName in L2Targets ? friendlyL2Target(groupName as L2Targets) : groupName}
              </span>
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Sample:</Label>
            <Value>
              {tooltipData.bar.data.bioNumber} ({tooltipData.bar.data.sampleId})
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Name:</Label>
            <Value>{tooltipData.bar.data.label}</Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>Gene:</Label>
            <Value>
              {tooltipData.key}
              {showAdmin && ` (${getGroup(tooltipData.key, 'assay') ?? tooltipData.key})`}
            </Value>
          </tr>
          <tr style={{ paddingBottom: '12px' }}>
            <Label>{valueName}:</Label>
            <Value>{friendlyAbundance(value)}</Value>
          </tr>
          {adminInfo && (
            <>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Chip:</Label>
                <Value>{adminInfo.chipInfo}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Ct values:</Label>
                <Value>
                  {adminInfo.ctsStyled}
                  {adminInfo.stdCt != null && (
                    <span style={{ fontStyle: 'italic' }}> (σ {adminInfo.stdCt.toFixed(2)})</span>
                  )}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Efficiencies:</Label>
                <Value>{adminInfo.effStyled}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Tm values:</Label>
                <Value>
                  {adminInfo.tmStyled}
                  {adminInfo.tmRangeText}
                </Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>QA rule:</Label>
                <Value>{adminInfo.statusStyled}</Value>
              </tr>
              <tr style={{ paddingBottom: '12px' }}>
                <Label>Flags:</Label>
                <Value>{adminInfo.flags.join(', ') || '-'}</Value>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

const Label = styled.td`
  width: 150;
  text-align: right;
`;

const Value = styled.td`
  ${oldTheme.fontWeight.bold}
  width: 150;
  text-align: left;
`;

interface BoxTooltipContentProps {
  tooltipData: BoxDatum;
}

export function BoxTooltipContent({ tooltipData }: BoxTooltipContentProps) {
  const normalisationMode = useNormalisationMode();
  const xAxisMode = useXAxisMode();
  const sampleLabel = xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? tooltipData.bioNumber : tooltipData.sampleNumber;
  const showExperimentalMetrics = !!useExperimentalMetric();
  return (
    <>
      <div style={{ color: oldTheme.darkGray, padding: 8 }}>
        <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
          <strong>Sample:</strong> {sampleLabel} ({tooltipData.sampleId})
        </div>
        <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
          <strong>Name: </strong> {tooltipData.label}
        </div>
        {tooltipData.date ? (
          <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
            <strong>Date:</strong> {friendlyDate(tooltipData.date as StandardDateFormat, 'prefixMon', true)}
          </div>
        ) : null}
      </div>
      <div style={{ fontSize: oldTheme.fontSize.s }}>
        {showExperimentalMetrics && (
          <>
            {Object.entries(tooltipData.experimentalMetrics || {}).map(([key, value]) => (
              <div key={key}>
                <b>{key}: </b>
                {(value as number | undefined)?.toFixed(key.startsWith('A') ? 1 : 0)}
              </div>
            ))}
            <br />
          </>
        )}
        <br />
        <table style={{ width: '100%' }}>
          <tbody>
            <tr>
              <td />
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>
                {NormalisedValueNameShort[normalisationMode]}&nbsp;
              </td>
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>log₁₀</td>
            </tr>
            {tooltipData.max === tooltipData.min && (
              <tr>
                <td style={{ textAlign: 'right' }} />
                <td>{tooltipData.max ? friendlyAbundance(10 ** tooltipData.max) : '-'}</td>
                <td>{tooltipData.max?.toFixed(6)}</td>
              </tr>
            )}
            {tooltipData.max !== tooltipData.min && (
              <>
                <tr>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 10 }}>T</span>&nbsp;&nbsp;&nbsp;&nbsp; upper
                  </td>
                  <td>{tooltipData.max ? friendlyAbundance(10 ** tooltipData.max) : '-'}</td>
                  <td>{tooltipData.max?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>Q3</td>
                  <td>{tooltipData.thirdQuartile ? friendlyAbundance(10 ** tooltipData.thirdQuartile) : '-'}</td>
                  <td>{tooltipData.thirdQuartile?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>×&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;mean</td>
                  <td>{tooltipData.mean ? friendlyAbundance(10 ** tooltipData.mean) : '-'}</td>
                  <td>{tooltipData.mean?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>—&nbsp;median</td>
                  <td>{tooltipData.median ? friendlyAbundance(10 ** tooltipData.median) : '-'}</td>
                  <td>{tooltipData.median?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>Q1</td>
                  <td>{tooltipData.firstQuartile ? friendlyAbundance(10 ** tooltipData.firstQuartile) : '-'}</td>
                  <td>{tooltipData.firstQuartile?.toFixed(6)}</td>
                </tr>
                <tr>
                  <td style={{ textAlign: 'right' }}>⊥&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;lower</td>
                  <td>{tooltipData.min ? friendlyAbundance(10 ** tooltipData.min) : '-'}</td>
                  <td>{tooltipData.min?.toFixed(6)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

const NormalisedValueNameShort = {
  [NormalisationMode.SIXTEEN_S]: 'Rel. abund.',
  [NormalisationMode.MG_SS]: 'Copy num. per mg SS',
  [NormalisationMode.HOUR]: 'Copy num. per hour',
  [NormalisationMode.LITRE]: 'Copy num. per L',
  [NormalisationMode.TEN_UL_DILUTED_DNA]: 'Copy num.',
  [NormalisationMode.MG_BOD]: 'Copy num. per mg BOD',
} as const;
